import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getInventory, saveInventoryBatch } from '../../api/inventory';
import { getBankAccounts } from '../../api/bankAccounts';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import { LopSearchModal } from '../../components/LopSearchModal';
import { useFunds } from '../../hooks/useFunds';
import { Search } from 'lucide-react';
import type { Inventory } from '../../types/Inventory';
import type { BankAccount } from '../../types/BankAccount';
import { useSources } from '../../hooks/useSources';
import { useInventoryRowTypes } from '../../hooks/useInventoryRowTypes';
import { t } from '../../i18n/t';
import './InventoryPage.css';
import { formatDateOnly, formatDateTimeIL } from '../../utils/dateTime';

// DATETIME2 ISO string → yyyy-mm-dd (date part only). Read-only display.
function fmtDate(p: ValueFormatterParams<Inventory>): string {
  return formatDateOnly(p.value);
}

function fmtDateTime(p: ValueFormatterParams<Inventory>): string {
  return formatDateTimeIL(p.value);
}

export function InventoryPage() {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  // '' = all accounts.
  const [acc, setAcc] = useState<string>('');
  const [accPickerOpen, setAccPickerOpen] = useState(false);
  const { data: funds } = useFunds();
  // "<acc> - <fund name>" options; typing filters by account number OR fund name.
  const accOptions = useMemo(() => {
    const fundName = new Map((funds ?? []).map((f) => [f.fundId, f.fundName]));
    return accounts.map((a) => ({ id: a.acc, name: (a.fundId != null && fundName.get(a.fundId)) || '' }));
  }, [accounts, funds]);
  const sources = useSources();
  const inventoryRowTypes = useInventoryRowTypes();

  // Account list for the filter. Degrades gracefully (empty → only "all") if the
  // viewer lacks bankAccounts View permission.
  useEffect(() => {
    let cancelled = false;
    getBankAccounts()
      .then((rows) => {
        if (!cancelled) setAccounts(rows);
      })
      .catch(() => {
        /* no accounts available to this user — only "all accounts" offered */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sourceNameById = useMemo(() => {
    const m = new Map<number, string>();
    (sources.data ?? []).forEach((s) => m.set(s.sourceId, s.sourceName));
    return m;
  }, [sources.data]);

  const rowTypeNameById = useMemo(() => {
    const m = new Map<number, string>();
    (inventoryRowTypes.data ?? []).forEach((rt) => m.set(rt.inventoryRowTypeId, rt.inventoryRowTypeName));
    return m;
  }, [inventoryRowTypes.data]);

  // getRows + saveBatch are keyed on `acc`: changing the account selector gives
  // useCrudGrid a new function identity, which re-fetches the grid (server-side
  // filter). saveBatch passes `acc` so the post-save reload stays scoped.
  const getRows = useCallback(() => getInventory(acc || undefined), [acc]);
  const saveBatch = useCallback(
    (added: Inventory[], modified: Inventory[], deletedIds: unknown[]) =>
      saveInventoryBatch(acc || undefined, added, modified, deletedIds as number[]),
    [acc],
  );

  const columnDefs = useMemo<ColDef<Inventory>[]>(
    () => [
      { field: 'acc', headerName: t('inventory.columns.acc'), width: 130 },
      { field: 'assetId', headerName: t('inventory.columns.assetId'), width: 120, cellDataType: 'number' },
      { field: 'assetName', headerName: t('inventory.columns.assetName'), width: 200, editable: false },
      { field: 'balanceTypeId', headerName: t('inventory.columns.balanceTypeId'), width: 110, cellDataType: 'number' },
      { field: 'qty', headerName: t('inventory.columns.qty'), width: 130, cellDataType: 'number' },
      { field: 'qty4Receive', headerName: t('inventory.columns.qty4Receive'), width: 120, cellDataType: 'number' },
      { field: 'inventorySourceId', headerName: t('inventory.columns.inventorySourceId'), width: 90, cellDataType: 'number' },
      {
        // Read-only resolved source name (client-side from useSources).
        colId: 'sourceName',
        headerName: t('inventory.columns.sourceName'),
        width: 150,
        editable: false,
        valueGetter: (p: ValueGetterParams<Inventory>) =>
          p.data ? sourceNameById.get(p.data.inventorySourceId) ?? '' : '',
      },
      { field: 'expirationDate', headerName: t('inventory.columns.expirationDate'), width: 120, editable: false, valueFormatter: fmtDate },
      { field: 'remark', headerName: t('inventory.columns.remark'), width: 160 },
      { field: 'executionDate', headerName: t('inventory.columns.executionDate'), width: 120, editable: false, valueFormatter: fmtDate },
      { field: 'forwardExecutionRate', headerName: t('inventory.columns.forwardExecutionRate'), width: 150, cellDataType: 'number' },
      { field: 'priceTypeId', headerName: t('inventory.columns.priceTypeId'), width: 100, cellDataType: 'number' },
      { field: 'inventoryRowTypeId', headerName: t('inventory.columns.inventoryRowTypeId'), width: 110, cellDataType: 'number' },
      {
        // Read-only resolved row-type name (client-side from useInventoryRowTypes).
        colId: 'inventoryRowTypeName',
        headerName: t('inventory.columns.inventoryRowTypeName'),
        width: 150,
        editable: false,
        valueGetter: (p: ValueGetterParams<Inventory>) =>
          p.data?.inventoryRowTypeId != null ? rowTypeNameById.get(p.data.inventoryRowTypeId) ?? '' : '',
      },
      { field: 'createdAt', headerName: t('inventory.columns.createdAt'), width: 150, editable: false, valueFormatter: fmtDateTime },
      { field: 'updatedAt', headerName: t('inventory.columns.updatedAt'), width: 150, editable: false, valueFormatter: fmtDateTime },
    ],
    [sourceNameById, rowTypeNameById],
  );

  // New row: prefill the selected account + the NOT-NULL DEFAULT-1 codes.
  const newRowDefaults = useCallback(
    (): Partial<Inventory> => ({
      acc: acc || '',
      assetId: 0,
      balanceTypeId: 1,
      qty: 0,
      inventorySourceId: 1,
      priceTypeId: 1,
    }),
    [acc],
  );

  return (
    <div className="inventory-page">
      <div className="inventory-page__filter">
        <label htmlFor="inventory-acc">{t('inventory.accountLabel')}</label>
        <div className="inventory-page__acc-picker">
          <LopSearchableSelect<string>
            id="inventory-acc"
            value={acc === '' ? null : acc}
            options={accOptions}
            onChange={(v) => setAcc(v ?? '')}
            allowNone
            noneLabel={t('inventory.allAccounts')}
            placeholder={t('inventory.allAccounts')}
          />
          <button
            type="button"
            className="inventory-page__acc-search"
            aria-label={t('inventory.searchAccount')}
            title={t('inventory.searchAccount')}
            onClick={() => setAccPickerOpen(true)}
          >
            <Search size={16} />
          </button>
        </div>
      </div>
      {accPickerOpen && (
        <LopSearchModal
          title={t('inventory.searchAccount')}
          table="BankAccounts"
          columns={[
            { field: 'acc', headerName: t('inventory.accountLabel') },
            { field: 'accName', headerName: t('inventory.accountName') },
            { field: 'fundId', headerName: t('inventory.accountFundId') },
            { field: 'bankId', headerName: t('inventory.accountBankId') },
          ]}
          onSelect={(row) => {
            const picked = row.acc;
            if (typeof picked === 'string' || typeof picked === 'number') setAcc(String(picked));
          }}
          onClose={() => setAccPickerOpen(false)}
        />
      )}
      <LopCrudPage<Inventory>
        resourceCode="inventory"
        titleKey="inventory.pageTitle"
        idField="inventoryId"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newRowDefaults}
      />
    </div>
  );
}
